import React from 'react';
import './ConversationSidebar.css';
import Avatar from './Avatar';
import { UserResponse } from '../utils/api';
import quantumEmotionLogo from '../assets/quantum_emotion_logo.jpg';

export interface Conversation {
  userId: string;
  username: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  isOnline?: boolean;
}

interface ConversationSidebarProps {
  currentUser: UserResponse | null;
  appName: string;
  appTagline: string;
  conversations: Conversation[];
  activeConversationId: string | null;
  searchQuery: string;
  searchResults: UserResponse[];
  isSearching: boolean;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
  onSelectConversation: (userId: string, username: string) => void;
  onSelectSearchResult: (user: UserResponse) => void;
  onEditUsername: () => void;
  onLogout: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  currentUser,
  appName,
  appTagline,
  conversations,
  activeConversationId,
  searchQuery,
  searchResults,
  isSearching,
  onSearchChange,
  onSearchSubmit,
  onSelectConversation,
  onSelectSearchResult,
  onEditUsername,
  onLogout,
}) => {
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchSubmit();
    }
  };

  return (
    <div className="sidebar">
      {/* Header with app branding and user profile */}
      <div className="sidebar__header">
        <div className="sidebar__brand">
          <img 
            src={quantumEmotionLogo} 
            alt="Quantum eMotion" 
            className="sidebar__logo-img"
          />
          <div className="sidebar__brand-info">
            <h1 className="sidebar__app-name">{appName}</h1>
            <span className="sidebar__app-tagline">{appTagline}</span>
          </div>
        </div>

        {currentUser && (
          <div className="sidebar__user-profile">
            <Avatar 
              name={currentUser.username || currentUser.userId} 
              size="medium"
              isOnline={true}
              showOnlineStatus={true}
            />
            <div className="sidebar__user-info">
              <div className="sidebar__user-name">
                <span className="sidebar__user-name-text">
                  {currentUser.username || currentUser.userId.substring(0, 8)}
                </span>
                <button 
                  className="sidebar__edit-btn"
                  onClick={onEditUsername}
                  title="Edit username"
                  aria-label="Edit username"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
              <div className="sidebar__encryption-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1C9.24 1 7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2h-1V6c0-2.76-2.24-5-5-5zm0 2c1.66 0 3 1.34 3 3v2H9V6c0-1.66 1.34-3 3-3zm0 10c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/>
                </svg>
                Secured
              </div>
            </div>
            <button 
              className="sidebar__logout-btn"
              onClick={onLogout}
              title="Log out"
              aria-label="Log out"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        )}

        {/* Search input */}
        <div className="sidebar__search">
          <div className="sidebar__search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </div>
          <input
            type="text"
            className="sidebar__search-input"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          {searchQuery && (
            <button 
              className="sidebar__search-clear"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Search results */}
      {searchResults.length > 0 && (
        <div className="sidebar__search-results">
          <div className="sidebar__section-label">Search Results</div>
          {searchResults.map((user) => (
            <div
              key={user.userId}
              className="sidebar__search-result"
              onClick={() => onSelectSearchResult(user)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && onSelectSearchResult(user)}
            >
              <Avatar name={user.username || user.userId} size="small" />
              <div className="sidebar__result-info">
                <span className="sidebar__result-name">{user.username}</span>
                <span className="sidebar__result-id">
                  ID: {user.userId.substring(0, 8)}...
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Conversation list */}
      <div className="sidebar__conversations">
        {conversations.length > 0 && (
          <div className="sidebar__section-label">Recent Conversations</div>
        )}
        
        {conversations.length === 0 && !searchQuery && (
          <div className="sidebar__empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <p>No conversations yet</p>
            <span>Search for users to start chatting</span>
          </div>
        )}

        {conversations.map((conv) => (
          <div
            key={conv.userId}
            className={`sidebar__conversation ${activeConversationId === conv.userId ? 'sidebar__conversation--active' : ''}`}
            onClick={() => onSelectConversation(conv.userId, conv.username)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && onSelectConversation(conv.userId, conv.username)}
          >
            <Avatar 
              name={conv.username || conv.userId} 
              size="medium"
              isOnline={conv.isOnline}
              showOnlineStatus={true}
            />
            <div className="sidebar__conversation-content">
              <div className="sidebar__conversation-header">
                <span className="sidebar__conversation-name">{conv.username}</span>
                {conv.lastMessageTime && (
                  <span className="sidebar__conversation-time">{conv.lastMessageTime}</span>
                )}
              </div>
              {conv.lastMessage && (
                <p className="sidebar__conversation-preview">{conv.lastMessage}</p>
              )}
            </div>
            {conv.unreadCount && conv.unreadCount > 0 && (
              <span className="sidebar__unread-badge">{conv.unreadCount}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversationSidebar;
